import React, { useRef, useState } from "react";
import { Stage, Layer, Image as KonvaImage, Rect, Text, Line, Arrow } from "react-konva";
import useImage from "use-image";
import { Box, Button, TextField, Dialog, DialogTitle, DialogContent } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { setAnnotations } from "store/slice/AppSlice";
import { getAssetannotationInfoByAssetidAPI } from "api/AssetannotationAPI";

// Usage: <ImageAnotation imageUrl="..." mode="EDIT" />

export default function ImageAnotation({
  imageUrl,
  width,
  height,
  viewportHeight,
  mode: propMode = "READ",
  onInjectObject,
  onDeleteObject,
  onEditObject,
}) {

  const dispatch = useDispatch();
  const [image] = useImage(imageUrl, "anonymous");
  const [rects, setRects] = useState([]);
  const [drawing, setDrawing] = useState(false);
  const [newRect, setNewRect] = useState(null);
  const [drawMode, setDrawMode] = useState("rect"); // "rect", "line", "arrow", "text", "highlight"
  const [textInput, setTextInput] = useState("");
  const [texts, setTexts] = useState([]);
  const [lines, setLines] = useState([]);
  const [currentLine, setCurrentLine] = useState(null);
  const [highlights, setHighlights] = useState([]);
  const [newHighlight, setNewHighlight] = useState(null);
  const [textDialogOpen, setTextDialogOpen] = useState(false);
  const [pendingText, setPendingText] = useState("");
  const [pendingTextPos, setPendingTextPos] = useState(null);
  const [hovered, setHovered] = useState({ type: null, index: null });
  const [nextCommentId, setNextCommentId] = useState(1);
  const [editTarget, setEditTarget] = useState(null); // { type, index }
  const [arrows, setArrows] = useState([]);
  const [currentArrow, setCurrentArrow] = useState(null);
  const [scale, setScale] = useState(1); // <-- local zoom state
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 }); // Add state for stage position
  const [scrollable, setScrollable] = useState(false); // Add a state to control scrollable mode

  const { annotations } = useSelector(state => state.appslice);
  const { productObj } = useSelector((state) => state.pharmaslice.productDashboard);

  const stageRef = useRef();
  const canvasWidth = width ?? image?.width ?? 1270;
  const canvasHeight = height ?? image?.height ?? 500;

  const transformArrowCommentObject = (annotationObj) => {
    return {
      id: annotationObj.id,
      commentId: annotationObj.commentId,
      type: annotationObj.annotationtype,
      points: annotationObj.title.split(',').map(Number),
      start: { x: annotationObj.xposition, y: annotationObj.yposition },
      text: ''
    };
  };
  React.useEffect(()=>{
    dispatch(setAnnotations([]));
    if (productObj.id) {
        let commentObjList = [];
        getAssetannotationInfoByAssetidAPI(productObj.qaassetid, (apiResponse) => {
            apiResponse.forEach(annotation => {
              let commentObj = {};
                if (annotation.annotationtype === 'arrow') {
                    commentObj = transformArrowCommentObject(annotation);
                } else {
                  commentObj = {
                    id: annotation.id,
                    commentId: annotation.annotationid,
                    height: annotation.height,
                    text: annotation.title,
                    type: annotation.annotationtype,
                    width: annotation.width,
                    x: annotation.xposition,
                    y: annotation.yposition
                  }
              }
                commentObjList.push(commentObj);
            });
            dispatch(setAnnotations(commentObjList));
           
        }, ()=>{})
    }
    },[])

  // Load initial comments if provided
  React.useEffect(() => {
    if (annotations && Array.isArray(annotations)) {
      setRects(annotations.filter(c => c.type === "rect"));
      setLines(annotations.filter(c => c.type === "line"));
      setHighlights(annotations.filter(c => c.type === "highlight"));
      setTexts(annotations.filter(c => c.type === "text"));
      setArrows(annotations.filter(c => c.type === "arrow"));
      // Optionally set nextCommentId to max existing commentId + 1
      const maxId = Math.max(0, ...annotations.map(c => c.commentId || 0));
      setNextCommentId(maxId + 1);
    }
    // Only run on mount
    // eslint-disable-next-line
  }, [annotations]);

  // Only allow drawing if in EDIT mode
  const getScaledPointerPosition = (stage) => {
    const pointer = stage.getPointerPosition();
    const scaleX = stage.scaleX() || 1;
    const scaleY = stage.scaleY() || 1;
    const x = (pointer.x - stage.x()) / scaleX;
    const y = (pointer.y - stage.y()) / scaleY;
    return { x, y };
  };

  const isCanvasTarget = (target) => {
    const targetName = target?.getClassName?.();
    return targetName === "Stage" || targetName === "Image";
  };

  const handleMouseDown = e => {
    if (propMode !== "EDIT") return;
    if (!isCanvasTarget(e.target)) return;
    const stage = e.target.getStage();
    const { x, y } = getScaledPointerPosition(stage);
    if (drawMode === "rect") {
      if (!drawing) {
        setNewRect({ x, y, width: 0, height: 0, text: "" });
        setDrawing(true);
      }
    }
    if (drawMode === "line") {
      setCurrentLine({ points: [x, y], text: "", start: { x, y } });
    }
    if (drawMode === "arrow") {
      setCurrentArrow({ points: [x, y], text: "", start: { x, y } });
    }
    if (drawMode === "highlight") {
      setNewHighlight({ x, y, width: 0, height: 0, text: "" });
      setDrawing(true);
    }
    if (drawMode === "text") {
      setPendingTextPos({ x, y });
      setTextDialogOpen(true);
    }
  };

  // Mouse move: update rectangle/line/highlight size
  const handleMouseMove = e => {
    const stage = e.target.getStage();
    const { x, y } = getScaledPointerPosition(stage);
    if (drawMode === "rect" && drawing && newRect) {
      setNewRect({
        ...newRect,
        width: x - newRect.x,
        height: y - newRect.y
      });
    }
    if (drawMode === "line" && currentLine) {
      setCurrentLine({
        ...currentLine,
        points: [...currentLine.points, x, y]
      });
    }
    if (drawMode === "arrow" && currentArrow) {
      setCurrentArrow({
        ...currentArrow,
        points: [currentArrow.start.x, currentArrow.start.y, x, y]
      });
    }
    if (drawMode === "highlight" && drawing && newHighlight) {
      setNewHighlight({
        ...newHighlight,
        width: x - newHighlight.x,
        height: y - newHighlight.y
      });
    }
  };

  // Mouse up: finish drawing and prompt for text if needed
  const handleMouseUp = () => {
    if (drawing && newRect && drawMode === "rect" && Math.abs(newRect.width) > 5 && Math.abs(newRect.height) > 5) {
      setDrawing(false);
      setNewRect(null);
      setRects(prev => [...prev, { ...newRect }]);
      setPendingTextPos({ x: newRect.x, y: newRect.y, type: "rect", index: rects.length });
      setPendingText("");
      setTextDialogOpen(true);
      return;
    }
    if (drawing && newHighlight && drawMode === "highlight" && Math.abs(newHighlight.width) > 5 && Math.abs(newHighlight.height) > 5) {
      setDrawing(false);
      setNewHighlight(null);
      setHighlights(prev => {
        const updated = [...prev, { ...newHighlight }];
        // Call onInjectObject for the new highlight
        if (onInjectObject) onInjectObject({ type: "highlight", ...newHighlight });
        return updated;
      });
      // No text popup for highlight
      return;
    }
    if (drawMode === "line" && currentLine) {
      setLines(prev => [...prev, { ...currentLine }]);
      setPendingTextPos({ x: currentLine.start.x, y: currentLine.start.y, type: "line", index: lines.length });
      setPendingText("");
      setTextDialogOpen(true);
      setCurrentLine(null);
      return;
    }
    if (drawMode === "arrow" && currentArrow) {
      console.log("Adding Arrow=>",currentArrow);
      if (currentArrow.points.length===4){
          setArrows(prev => {
            const updated = [...prev, { ...currentArrow }];
            // Call onInjectObject for the new arrow
            if (onInjectObject) onInjectObject({ type: "arrow", ...currentArrow });
            return updated;
          });
      }        
      // No text popup for arrow
      setCurrentArrow(null);
      return;
    }
    setDrawing(false);
    setNewRect(null);
    setNewHighlight(null);
  };

  // Save text to the last drawn shape or as a standalone text
  const handleTextSave = () => {
    if (editTarget) {
      let editedObj = null;
      if (editTarget.type === "rect") {
        setRects(prev => {
          const updated = [...prev];
          updated[editTarget.index] = { ...updated[editTarget.index], text: pendingText };
          editedObj = { type: "rect", ...updated[editTarget.index] };
          if (onEditObject && editedObj) onEditObject(editedObj);
          return updated;
        });
      } else if (editTarget.type === "highlight") {
        setHighlights(prev => {
          const updated = [...prev];
          updated[editTarget.index] = { ...updated[editTarget.index], text: pendingText };
          editedObj = { type: "highlight", ...updated[editTarget.index] };
          if (onEditObject && editedObj) onEditObject(editedObj);
          return updated;
        });
      } else if (editTarget.type === "line") {
        setLines(prev => {
          const updated = [...prev];
          updated[editTarget.index] = { ...updated[editTarget.index], text: pendingText };
          editedObj = { type: "line", ...updated[editTarget.index] };
          if (onEditObject && editedObj) onEditObject(editedObj);
          return updated;
        });
      } else if (editTarget.type === "arrow") {
        setArrows(prev => {
          const updated = [...prev];
          updated[editTarget.index] = { ...updated[editTarget.index], text: pendingText };
          editedObj = { type: "arrow", ...updated[editTarget.index] };
          if (onEditObject && editedObj) onEditObject(editedObj);
          return updated;
        });
      } else if (editTarget.type === "text") {
        setTexts(prev => {
          const updated = [...prev];
          updated[editTarget.index] = { ...updated[editTarget.index], text: pendingText };
          editedObj = { type: "text", ...updated[editTarget.index] };
          if (onEditObject && editedObj) onEditObject(editedObj);
          return updated;
        });
      }
      setEditTarget(null);
      setTextDialogOpen(false);
      setPendingText("");
      setPendingTextPos(null);
      return;
    }

    let injectedComment = null;
    if (pendingTextPos && pendingTextPos.type === "rect") {
      setRects(prev => {
        const updated = [...prev];
        const id = nextCommentId;
        updated[pendingTextPos.index] = { ...updated[pendingTextPos.index], text: pendingText, commentId: id };
        injectedComment = { type: "rect", ...updated[pendingTextPos.index] };
        setNextCommentId(id + 1);
        if (onInjectObject && injectedComment) onInjectObject(injectedComment);
        return updated;
      });
    } else if (pendingTextPos && pendingTextPos.type === "highlight") {
      setHighlights(prev => {
        const updated = [...prev];
        const id = nextCommentId;
        updated[pendingTextPos.index] = { ...updated[pendingTextPos.index], text: pendingText, commentId: id };
        injectedComment = { type: "highlight", ...updated[pendingTextPos.index] };
        setNextCommentId(id + 1);
        if (onInjectObject && injectedComment) onInjectObject(injectedComment);
        return updated;
      });
    } else if (pendingTextPos && pendingTextPos.type === "line") {
      setLines(prev => {
        const updated = [...prev];
        const id = nextCommentId;
        updated[pendingTextPos.index] = { ...updated[pendingTextPos.index], text: pendingText, commentId: id };
        injectedComment = { type: "line", ...updated[pendingTextPos.index] };
        setNextCommentId(id + 1);
        if (onInjectObject && injectedComment) onInjectObject(injectedComment);
        return updated;
      });
    } else if (pendingTextPos && pendingTextPos.type === "arrow") {
      setArrows(prev => {
        const updated = [...prev];
        const id = nextCommentId;
        updated[pendingTextPos.index] = { ...updated[pendingTextPos.index], text: pendingText, commentId: id };
        injectedComment = { type: "arrow", ...updated[pendingTextPos.index] };
        setNextCommentId(id + 1);
        if (onInjectObject && injectedComment) onInjectObject(injectedComment);
        return updated;
      });
    } else if (drawMode === "text" && pendingTextPos) {
      const id = nextCommentId;
      const newText = { type: "text", x: pendingTextPos.x, y: pendingTextPos.y, text: pendingText, commentId: id };
      setTexts(prev => {
        setNextCommentId(id + 1);
        if (onInjectObject) onInjectObject(newText);
        return [...prev, { x: pendingTextPos.x, y: pendingTextPos.y, text: pendingText, commentId: id }];
      });
    }
    setTextDialogOpen(false);
    setPendingText("");
    setPendingTextPos(null);
  };

  // Clear all annotations
  const handleClear = () => {
    setRects([]);
    setLines([]);
    setTexts([]);
    setHighlights([]);
  };

  // Dump all comments/texts with their coordinates
  const handleDumpComments = () => {
    const comments = [
      ...rects.filter(r => r.text).map(r => ({ type: "rect", text: r.text, x: r.x, y: r.y, width: r.width, height: r.height })),
      ...lines.filter(l => l.text).map(l => ({ type: "line", text: l.text, x: l.start.x, y: l.start.y, points: l.points })),
      ...highlights.filter(h => h.text).map(h => ({ type: "highlight", text: h.text, x: h.x, y: h.y, width: h.width, height: h.height })),
      ...texts.map(t => ({ type: "text", text: t.text, x: t.x, y: t.y }))
    ];
    if (onInjectObject) onInjectObject(comments);
    console.log("All comments with coordinates:", comments);
  };

  // Delete object and call callback
  const deleteObject = (type, index) => {
    let deleted;
    if (type === "rect") {
      deleted = { type, ...rects[index] };
      setRects(prev => prev.filter((_, i) => i !== index));
    }
    if (type === "line") {
      deleted = { type, ...lines[index] };
      setLines(prev => prev.filter((_, i) => i !== index));
    }
    if (type === "arrow") {
      deleted = { type, ...arrows[index] };
      setArrows(prev => prev.filter((_, i) => i !== index));
    }
    if (type === "highlight") {
      deleted = { type, ...highlights[index] };
      setHighlights(prev => prev.filter((_, i) => i !== index));
    }
    if (type === "text") {
      deleted = { type, ...texts[index] };
      setTexts(prev => prev.filter((_, i) => i !== index));
    }
    if (onDeleteObject && deleted) onDeleteObject(deleted);
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: viewportHeight ?? "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column"
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowX: "auto",
          overflowY: "auto",
          border: "1px solid #ccc",
          background: "#fafafa"
        }}
      >
        <Box
          sx={{
            width: "max-content",
            minWidth: "100%"
          }}
        >
          <Stage
            width={canvasWidth}
            height={canvasHeight}
            ref={stageRef}
            style={{
              display: "block",
              width: `${canvasWidth}px`,
              height: `${canvasHeight}px`,
              minWidth: "100%",
              cursor: scale > 1 && scrollable ? "grab" : "default"
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            scaleX={scale}
            scaleY={scale}
            x={stagePos.x}
            y={stagePos.y}
            draggable={scale > 1 && scrollable}
            onDragEnd={e => {
              setStagePos({ x: e.target.x(), y: e.target.y() });
            }}
          >
            <Layer>
              {image && (
                <KonvaImage
                  image={image}
                  width={canvasWidth}
                  height={canvasHeight}
                />
              )}
            {rects.map((rect, i) => (
              <React.Fragment key={i}>
                {hovered.type === "rect" && hovered.index === i && (
                  <Rect
                    x={rect.x}
                    y={rect.y}
                    width={rect.width}
                    height={rect.height}
                    fill="#f5f5f5"
                    opacity={1}
                    draggable={false}
                  />
                )}
                <Rect
                  x={rect.x}
                  y={rect.y}
                  width={rect.width}
                  height={rect.height}
                  stroke="red"
                  strokeWidth={2}
                  dash={[6, 4]}
                  draggable={false}
                  onMouseOver={() => setHovered({ type: "rect", index: i })}
                  onMouseOut={() => setHovered({ type: null, index: null })}
                />
                {rect.text && (
                  <>
                    <Text
                      x={rect.x + 5}
                      y={rect.y + 5}
                      text={rect.text}
                      fontSize={18}
                      fill="red"
                      listening={true}
                      onClick={e => {
                        if (propMode !== "EDIT") return;
                        e.cancelBubble = true;
                        if (e.evt && e.evt.stopPropagation) e.evt.stopPropagation();
                        setPendingText(rect.text);
                        setEditTarget({ type: "rect", index: i });
                        setTextDialogOpen(true);
                      }}
                      onMouseOver={e => {
                        if (propMode === "EDIT") {
                          const stage = e.target.getStage();
                          if (stage && stage.container()) {
                            stage.container().style.cursor = "pointer";
                          }
                        }
                      }}
                      onMouseOut={e => {
                        if (propMode === "EDIT") {
                          const stage = e.target.getStage();
                          if (stage && stage.container()) {
                            stage.container().style.cursor = "default";
                          }
                        }
                      }}
                    />
                    {propMode === "EDIT" && (
                      <Text
                        x={rect.x + 5 + (rect.text.length * 10) + 10}
                        y={rect.y + 5}
                        text="✕"
                        fontSize={18}
                        fill="black"
                        onClick={e => {
                          e.cancelBubble = true;
                          if (e.evt && e.evt.stopPropagation) e.evt.stopPropagation();
                          deleteObject("rect", i);
                        }}
                        onMouseOver={e => {
                          const stage = e.target.getStage();
                          if (stage && stage.container()) {
                            stage.container().style.cursor = "pointer";
                          }
                        }}
                        onMouseOut={e => {
                          const stage = e.target.getStage();
                          if (stage && stage.container()) {
                            stage.container().style.cursor = "default";
                          }
                        }}
                      />
                    )}
                  </>
                )}
              </React.Fragment>
            ))}
            {newRect && (
              <Rect
                x={newRect.x}
                y={newRect.y}
                width={newRect.width}
                height={newRect.height}
                stroke="blue"
                strokeWidth={2}
                dash={[4, 2]}
                draggable={false}
              />
            )}
            {lines.map((line, i) => (
              <React.Fragment key={i}>
                <Line points={line.points} stroke="green" strokeWidth={2} />
                {line.text && (
                  <>
                    <Text
                      x={line.start.x + 5}
                      y={line.start.y + 5}
                      text={line.text}
                      fontSize={18}
                      fill="green"
                      listening={true}
                      onClick={e => {
                        if (propMode !== "EDIT") return;
                        e.cancelBubble = true;
                        if (e.evt && e.evt.stopPropagation) e.evt.stopPropagation();
                        setPendingText(line.text);
                        setEditTarget({ type: "line", index: i });
                        setTextDialogOpen(true);
                      }}
                      onMouseOver={e => {
                        const stage = e.target.getStage();
                        if (stage && stage.container()) {
                          stage.container().style.cursor = "pointer";
                        }
                      }}
                      onMouseOut={e => {
                        const stage = e.target.getStage();
                        if (stage && stage.container()) {
                          stage.container().style.cursor = "default";
                        }
                      }}
                    />
                    {propMode === "EDIT" && (
                      <Text
                        x={line.start.x + 5 + (line.text.length * 10) + 10}
                        y={line.start.y + 5}
                        text="✕"
                        fontSize={18}
                        fill="black"
                        onClick={e => {
                          e.cancelBubble = true;
                          if (e.evt && e.evt.stopPropagation) e.evt.stopPropagation();
                          deleteObject("line", i);
                        }}
                        onMouseOver={e => {
                          const stage = e.target.getStage();
                          if (stage && stage.container()) {
                            stage.container().style.cursor = "pointer";
                          }
                        }}
                        onMouseOut={e => {
                          const stage = e.target.getStage();
                          if (stage && stage.container()) {
                            stage.container().style.cursor = "default";
                          }
                        }}
                      />
                    )}
                  </>
                )}
              </React.Fragment>
            ))}
            {currentLine && (
              <Line points={currentLine.points} stroke="blue" strokeWidth={2} />
            )}
            {texts.map((t, i) => (
              <React.Fragment key={i}>
                <Text
                  x={t.x}
                  y={t.y}
                  text={t.text}
                  fontSize={18}
                  fill="blue"
                  listening={true}
                  onClick={e => {
                    if (propMode !== "EDIT") return;
                    e.cancelBubble = true;
                    if (e.evt && e.evt.stopPropagation) e.evt.stopPropagation();
                    setPendingText(t.text);
                    setEditTarget({ type: "text", index: i });
                    setTextDialogOpen(true);
                  }}
                  onMouseOver={e => {
                    const stage = e.target.getStage();
                    if (stage && stage.container()) {
                      stage.container().style.cursor = "pointer";
                    }
                  }}
                  onMouseOut={e => {
                    const stage = e.target.getStage();
                    if (stage && stage.container()) {
                      stage.container().style.cursor = "default";
                    }
                  }}
                />
                {propMode === "EDIT" && (
                  <Text
                    x={t.x + (t.text.length * 10) + 10}
                    y={t.y}
                    text="✕"
                    fontSize={18}
                    fill="black"
                    onClick={e => {
                      e.cancelBubble = true;
                      if (e.evt && e.evt.stopPropagation) e.evt.stopPropagation();
                      deleteObject("text", i);
                    }}
                    onMouseOver={e => {
                      const stage = e.target.getStage();
                      if (stage && stage.container()) {
                        stage.container().style.cursor = "pointer";
                      }
                    }}
                    onMouseOut={e => {
                      const stage = e.target.getStage();
                      if (stage && stage.container()) {
                        stage.container().style.cursor = "default";
                      }
                    }}
                  />
                )}
              </React.Fragment>
            ))}
            {highlights.map((hl, i) => (
              <React.Fragment key={i}>
                {/* Highlight shape */}
                <Rect
                  x={hl.x}
                  y={hl.y}
                  width={hl.width}
                  height={hl.height}
                  fill="yellow"
                  opacity={0.4}
                  draggable={false}
                  onMouseOver={() => setHovered({ type: "highlight", index: i })}
                  onMouseOut={() => setHovered({ type: null, index: null })}
                />
                {/* Show comment background and text if there's a comment */}
                {hl.text && (
                  <>
                    <Rect
                      x={hl.x + 5}
                      y={hl.y + 5}
                      width={hl.text.length * 10 + 36}
                      height={32}
                      fill="#fffbe6"
                      opacity={1}
                      cornerRadius={6}
                      shadowColor="#888"
                      shadowBlur={4}
                      shadowOffset={{ x: 2, y: 2 }}
                      listening={false}
                    />
                    <Text
                      x={hl.x + 5}
                      y={hl.y + 5}
                      text={hl.text}
                      fontSize={18}
                      fill="orange"
                      listening={true}
                      onClick={e => {
                        e.cancelBubble = true;
                        if (e.evt && e.evt.stopPropagation) e.evt.stopPropagation();
                        setPendingText(hl.text);
                        setEditTarget({ type: "highlight", index: i });
                        setTextDialogOpen(true);
                      }}
                      onMouseOver={e => {
                        const stage = e.target.getStage();
                        if (stage && stage.container()) {
                          stage.container().style.cursor = "pointer";
                        }
                      }}
                      onMouseOut={e => {
                        const stage = e.target.getStage();
                        if (stage && stage.container()) {
                          stage.container().style.cursor = "default";
                        }
                      }}
                    />
                  </>
                )}
                {/* X icon for delete, always shown in EDIT mode */}
                {propMode === "EDIT" && (
                  <Text
                    x={hl.x + hl.width + 10}
                    y={hl.y - 10}
                    text="✕"
                    fontSize={18}
                    fill="black"
                    listening={true}
                    onClick={e => {
                      e.cancelBubble = true;
                      if (e.evt && e.evt.stopPropagation) e.evt.stopPropagation();
                      deleteObject("highlight", i);
                    }}
                    onMouseOver={e => {
                      const stage = e.target.getStage();
                      if (stage && stage.container()) {
                        stage.container().style.cursor = "pointer";
                      }
                    }}
                    onMouseOut={e => {
                      const stage = e.target.getStage();
                      if (stage && stage.container()) {
                        stage.container().style.cursor = "default";
                      }
                    }}
                  />
                )}
              </React.Fragment>
            ))}
            {newHighlight && (
              <Rect
                x={newHighlight.x}
                y={newHighlight.y}
                width={newHighlight.width}
                height={newHighlight.height}
                fill="yellow"
                opacity={0.4}
                draggable={false}
              />
            )}
            {arrows.map((arrow, i) => (
              <React.Fragment key={i}>
                <Arrow
                  points={
                    Array.isArray(arrow.points) 
                      ? arrow.points
                      : [0, 0, 0, 0]
                  }
                  stroke="purple"
                  strokeWidth={3}
                  pointerLength={15}
                  pointerWidth={15}
                />
                {/* X icon for delete */}
                {propMode === "EDIT" && (
                  <Text
                    x={arrow.points[2] + 20}
                    y={arrow.points[3] - 10}
                    text="✕"
                    fontSize={18}
                    fill="black"
                    listening={true}
                    onClick={e => {
                      e.cancelBubble = true;
                      if (e.evt && e.evt.stopPropagation) e.evt.stopPropagation();
                      deleteObject("arrow", i);
                    }}
                    onMouseOver={e => {
                      const stage = e.target.getStage();
                      if (stage && stage.container()) {
                        stage.container().style.cursor = "pointer";
                      }
                    }}
                    onMouseOut={e => {
                      const stage = e.target.getStage();
                      if (stage && stage.container()) {
                        stage.container().style.cursor = "default";
                      }
                    }}
                  />
                )}
              </React.Fragment>
            ))}
              {currentArrow && (
                <Arrow
                  points={currentArrow.points}
                  stroke="blue"
                  strokeWidth={2}
                  pointerLength={15}
                  pointerWidth={15}
                />
              )}
            </Layer>
          </Stage>
        </Box>
      </Box>
      {/* Show annotation buttons only in EDIT mode */}
      {propMode === "EDIT" && (
        <Box
          sx={{
            mt: 2,
            pt: 1,
            borderTop: "1px solid #ddd",
            background: "secondary.lighter",
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            flexShrink: 0
          }}
        >
          {/* Zoom In/Out buttons */}
          <Button
            variant="outlined"
            onClick={() => {
              setScale(s => Math.min(s + 0.1, 3));
              setDrawMode("");
              setScrollable(true); // Enable scrollable mode
            }}
          >
            Zoom In
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setScale(s => Math.max(s - 0.1, 0.2));
              setDrawMode("");
              setScrollable(true); // Enable scrollable mode
            }}
          >
            Zoom Out
          </Button>
          <Button
            variant={drawMode === "rect" ? "contained" : "outlined"}
            color={drawMode === "rect" ? "primary" : "inherit"}
            onClick={() => {
              setDrawMode("rect");
              setScrollable(false); // Disable scrollable mode
            }}
          >
            Rectangle
          </Button>
          
          <Button
            variant={drawMode === "highlight" ? "contained" : "outlined"}
            color={drawMode === "highlight" ? "primary" : "inherit"}
            onClick={() => {
              setDrawMode("highlight");
              setScrollable(false); // Disable scrollable mode
            }}
          >
            Highlight
          </Button>
          <Button
            variant={drawMode === "arrow" ? "contained" : "outlined"}
            color={drawMode === "arrow" ? "primary" : "inherit"}
            onClick={() => {
              setDrawMode("arrow");
              setScrollable(false); // Disable scrollable mode
            }}
          >
            Arrow
          </Button>
          {/* <Button
            variant="contained"
            color="success"
            onClick={handleDumpComments}
          >
            Save Comments
          </Button> */}
        </Box>
      )}
      <Dialog
        open={textDialogOpen}
        onClose={() => setTextDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editTarget ? "Edit Comment" : "Enter Comment"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Text"
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            value={pendingText}
            onChange={e => setPendingText(e.target.value)}
          />
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              onClick={() => {
                if (pendingTextPos) {
                  if (pendingTextPos.type === "rect") {
                    setRects(prev => prev.filter((_, i) => i !== pendingTextPos.index));
                  } else if (pendingTextPos.type === "highlight") {
                    setHighlights(prev => prev.filter((_, i) => i !== pendingTextPos.index));
                  } else if (pendingTextPos.type === "line") {
                    setLines(prev => prev.filter((_, i) => i !== pendingTextPos.index));
                  } else if (drawMode === "text") {
                    // No need to remove, as text is only added on Save
                  }
                }
                setTextDialogOpen(false);
                setPendingText("");
                setPendingTextPos(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleTextSave}
              disabled={!pendingText.trim()}
            >
              Save
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
